import { AccessTime, Lock } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "react-admin";
import { Link } from "react-router-dom";

import { LearningCourseSummary, useLearningCatalog, useMyLearning } from "../../../hooks/useLearningCatalog";
import { resolveLearningMediaUrl } from "../../../lib/learning/mediaReference";
import { borderRadius, color, shadows, space } from "../../../theme/designTokens";

const thumbnailUrl = (filename: string): string | undefined => {
  try {
    return resolveLearningMediaUrl(filename);
  } catch {
    return undefined;
  }
};

const CourseCardContent = ({ course }: { course: LearningCourseSummary }) => {
  const translate = useTranslate();
  const image = thumbnailUrl(course.thumbnailFilename);
  return (
    <>
      {image && <CardMedia component="img" height="168" image={image} alt="" sx={{ objectFit: "cover" }} />}
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: space.md, minHeight: 154 }}>
        <Typography component="h3" variant="h6">
          {course.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {course.description}
        </Typography>
        <Stack direction="row" gap={space.sm2} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            icon={<AccessTime />}
            label={translate("learning.duration", { minutes: course.estimatedDurationMinutes })}
          />
          {course.locked && <Chip size="small" icon={<Lock />} label={translate("learning.locked")} />}
        </Stack>
      </CardContent>
    </>
  );
};

const CourseCard = ({ course }: { course: LearningCourseSummary }) => (
  <Card
    data-testid={`learning-course-${course.id}`}
    variant="outlined"
    sx={{ height: "100%", borderRadius: borderRadius.md, borderColor: color.panelBorder, boxShadow: shadows.card }}
  >
    {course.locked ? (
      <Box aria-disabled="true" sx={{ height: "100%", opacity: 0.72 }}>
        <CourseCardContent course={course} />
      </Box>
    ) : (
      <CardActionArea component={Link} to={`/learning/courses/${course.id}`} sx={{ height: "100%" }}>
        <CourseCardContent course={course} />
      </CardActionArea>
    )}
  </Card>
);

// The catalog shows only content the caregiver was assigned: directly
// accessible courses plus the learning paths a manager assigned (or an
// earlier enrollment established). Organization-granted content without an
// assignment stays hidden — there is no self-enrollment.
export const CourseList = () => {
  const translate = useTranslate();
  const { courses, learningPaths, loading, error, reload } = useLearningCatalog();
  const learning = useMyLearning();

  if ((loading || learning.loading) && courses.length === 0) {
    return (
      <Box display="grid" minHeight={320} sx={{ placeItems: "center" }}>
        <CircularProgress aria-label={translate("learning.catalog.loading")} />
      </Box>
    );
  }

  if (learning.error || (error && courses.length === 0)) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => void Promise.allSettled([reload(), learning.reload()])}>
            {translate("learning.retry")}
          </Button>
        }
      >
        {translate("learning.catalog.error")}
      </Alert>
    );
  }

  const coursesById = new Map(courses.map(course => [course.id, course]));
  const enrolledPathIds = new Set(learning.pathSources.map(source => source.pathId));
  const assignedPaths = learningPaths.filter(path => enrolledPathIds.has(path.id));
  const assignedCourses = courses.filter(course => course.directAccess);

  if (assignedCourses.length === 0 && assignedPaths.length === 0) {
    return (
      <Box data-testid="learning-catalog-empty" py={space.xxxl} textAlign="center">
        <Typography component="h1" variant="h4" gutterBottom>
          {translate("learning.title")}
        </Typography>
        <Typography color="text.secondary">{translate("learning.catalog.empty")}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={space.xxl1}>
      <Box>
        <Typography component="h1" variant="h4" gutterBottom>
          {translate("learning.title")}
        </Typography>
        <Typography color="text.secondary">{translate("learning.catalog.intro")}</Typography>
      </Box>

      {assignedPaths.map(path => {
        const pathCourses = [...path.courses]
          .sort((left, right) => left.position - right.position)
          .flatMap(reference => {
            const course = coursesById.get(reference.courseId);
            return course ? [course] : [];
          });
        if (pathCourses.length === 0) return null;
        return (
          <Box component="section" key={path.id} data-testid={`learning-path-${path.id}`}>
            <Typography component="h2" variant="h5" gutterBottom>
              {path.title}
            </Typography>
            <Typography color="text.secondary" mb={space.xl}>
              {path.description}
            </Typography>
            <Grid container spacing={space.xl}>
              {pathCourses.map(course => (
                <Grid item xs={12} sm={6} md={4} key={`${path.id}:${course.id}`}>
                  <CourseCard course={course} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {assignedCourses.length > 0 && (
        <Box component="section">
          <Typography component="h2" variant="h5" gutterBottom>
            {translate("learning.catalog.myCourses")}
          </Typography>
          <Grid container spacing={space.xl}>
            {assignedCourses.map(course => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <CourseCard course={course} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Stack>
  );
};
